import { NextRequest, NextResponse } from 'next/server';

import { locales } from '../i18n/routing';

export const LOCALE_COOKIE_NAME = 'NEXT_LOCALE';
export const LOCALE_STORAGE_KEY = 'user_preferred_locale';

// Cookie duration: 1 year in seconds
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 31536000 seconds = 1 year

export const localeCookieOptions = {
  httpOnly: false, // Must be accessible from client-side JS
  sameSite: 'lax' as const,
  secure: process.env.NODE_ENV === 'production',
  path: '/',
  maxAge: COOKIE_MAX_AGE
} as const;

/**
 * Get the expiration date for the locale cookie (1 year from now)
 * This ensures the cookie persists across browser sessions
 */
function getExpiresDate(): Date {
  const date = new Date();
  date.setTime(date.getTime() + COOKIE_MAX_AGE * 1000); // Convert seconds to milliseconds
  return date;
}

/**
 * Get locale preference from cookie (server-side)
 * @param request NextRequest object
 * @returns Locale string ('en' or 'fr') or null if not set/invalid
 */
export function getLocaleCookie(request: NextRequest): string | null {
  const cookie = request.cookies.get(LOCALE_COOKIE_NAME);
  if (!cookie?.value) {
    return null;
  }

  const locale = cookie.value;
  // Validate that it's a supported locale
  if (locales.includes(locale as (typeof locales)[number])) {
    return locale;
  }

  return null;
}

/**
 * Set locale preference cookie (server-side)
 * @param response NextResponse object
 * @param locale Locale string ('en' or 'fr')
 */
export function setLocaleCookie(response: NextResponse, locale: string): void {
  // Validate locale
  if (!locales.includes(locale as (typeof locales)[number])) {
    return;
  }

  // Set cookie with explicit expires date to ensure it persists across browser sessions
  response.cookies.set(LOCALE_COOKIE_NAME, locale, {
    ...localeCookieOptions,
    expires: getExpiresDate()
  });
}

/**
 * Clear locale preference cookie (server-side)
 * @param response NextResponse object
 */
export function clearLocaleCookie(response: NextResponse): void {
  response.cookies.delete(LOCALE_COOKIE_NAME);
}

/**
 * Set locale preference using DUAL STORAGE (localStorage + cookie)
 * localStorage is the primary source of truth as it ALWAYS persists across browser sessions
 * Cookie is set as a backup for server-side middleware access
 *
 * @param locale Locale string ('en' or 'fr')
 * @returns boolean indicating success
 */
export function setLocaleCookieClient(locale: string): boolean {
  try {
    if (typeof window === 'undefined') {
      return false;
    }

    // Validate locale
    if (!locales.includes(locale as (typeof locales)[number])) {
      console.error(`Invalid locale: ${locale}`);
      return false;
    }

    // PRIMARY STORAGE: Use localStorage (ALWAYS persists)
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, locale);
      console.log('✅ Locale saved to localStorage:', locale);
    } catch (storageError) {
      console.error('Failed to save to localStorage:', storageError);
      // Continue anyway to try cookie
    }

    // SECONDARY STORAGE: Set cookie for server-side access
    try {
      const expires = getExpiresDate();
      const cookieParts = [
        `${LOCALE_COOKIE_NAME}=${locale}`,
        `Path=${localeCookieOptions.path}`,
        `Max-Age=${localeCookieOptions.maxAge}`,
        `Expires=${expires.toUTCString()}`,
        `SameSite=${localeCookieOptions.sameSite}`
      ];

      if (localeCookieOptions.secure) {
        cookieParts.push('Secure');
      }

      document.cookie = cookieParts.join('; ');
      console.log('✅ Locale cookie set:', {
        name: LOCALE_COOKIE_NAME,
        value: locale,
        expires: expires.toUTCString()
      });
    } catch (cookieError) {
      console.error('Failed to set cookie:', cookieError);
      // Not critical if cookie fails, localStorage is primary
    }

    return true;
  } catch (error) {
    console.error('Failed to set locale preference:', error);
    return false;
  }
}

/**
 * Get locale preference from DUAL STORAGE (localStorage first, then cookie)
 * Checks localStorage first (primary), then falls back to cookie
 * Returns null if no stored preference exists (does not auto-detect from browser)
 *
 * @returns Locale string ('en' or 'fr') or null if not set/invalid
 */
export function getLocaleCookieClient(): string | null {
  try {
    if (typeof window === 'undefined') {
      return null;
    }

    // PRIMARY: Check localStorage first
    try {
      const storedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
      if (storedLocale && locales.includes(storedLocale as (typeof locales)[number])) {
        console.log('✅ Locale found in localStorage:', storedLocale);
        return storedLocale;
      }
    } catch (storageError) {
      console.warn('Could not access localStorage:', storageError);
      // Continue to cookie fallback
    }

    // FALLBACK: Check cookie
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === LOCALE_COOKIE_NAME && value) {
        if (locales.includes(value as (typeof locales)[number])) {
          console.log('✅ Locale found in cookie:', value);
          // Sync to localStorage for future use
          try {
            localStorage.setItem(LOCALE_STORAGE_KEY, value);
          } catch {
            // Ignore storage errors
          }
          return value;
        }
      }
    }

    console.log('ℹ️ No locale preference found in storage');
    return null;
  } catch (error) {
    console.error('Failed to get locale preference:', error);
    return null;
  }
}

/**
 * Get locale preference with browser fallback
 * Checks localStorage first, then cookie, then detects from browser
 *
 * @returns Locale string ('en' or 'fr') - never returns null
 */
export function getLocaleWithBrowserFallback(): string {
  const storedLocale = getLocaleCookieClient();
  if (storedLocale) {
    return storedLocale;
  }

  // No stored preference, detect from browser
  return getBrowserPreferredLocaleClient();
}

/**
 * Detects preferred locale from browser's navigator.language(s)
 * Priority: If French exists anywhere in preferences, use French
 * Otherwise, use the highest priority supported language
 * @returns Locale string ('en' or 'fr')
 */
export function getBrowserPreferredLocaleClient(): string {
  if (typeof window === 'undefined') {
    return 'fr';
  }

  // Get all browser languages (e.g., ["en-US", "en", "fr"])
  const browserLanguages = navigator.languages?.length ? [...navigator.languages] : [navigator.language];

  // Parse to base language codes
  const languages = browserLanguages.map((lang) => lang?.split('-')[0]?.toLowerCase()).filter(Boolean);

  // Priority: If French exists anywhere, use French
  if (languages.includes('fr')) {
    return 'fr';
  }

  // Otherwise, use highest priority supported language
  for (const lang of languages) {
    if (lang === 'en') {
      return 'en';
    }
  }

  return 'fr'; // Default
}

/**
 * Clear locale preference from BOTH localStorage and cookie
 * @returns boolean indicating success
 */
export function clearLocaleCookieClient(): boolean {
  try {
    if (typeof window === 'undefined') {
      return false;
    }

    // Clear localStorage
    try {
      localStorage.removeItem(LOCALE_STORAGE_KEY);
      console.log('✅ Locale cleared from localStorage');
    } catch (storageError) {
      console.warn('Could not clear localStorage:', storageError);
    }

    // Clear cookie
    document.cookie = `${LOCALE_COOKIE_NAME}=; Path=${localeCookieOptions.path}; Max-Age=0`;
    console.log('✅ Locale cookie cleared');

    return true;
  } catch (error) {
    console.error('Failed to clear locale preference:', error);
    return false;
  }
}
